import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore, usePatientStore } from '@/stores';
import { supabase } from '@/lib/supabase';
import { fetchSupabase } from '@/lib/supabase';
import { AppLayout } from '@/components/layout';

// Public Pages
import LandingPage from '@/pages/LandingPage';

// Auth Pages
import { LoginPage, RegisterPage } from '@/pages/auth';
import { PasswordRecoveryPage } from '@/pages/auth/PasswordRecoveryPage';

// Load each workspace screen only when it is opened.
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PatientsListPage = lazy(() => import('@/pages/patients/PatientsListPage').then(m => ({ default: m.PatientsListPage })));
const PatientFormPage = lazy(() => import('@/pages/patients/PatientFormPage').then(m => ({ default: m.PatientFormPage })));
const PatientDetailPage = lazy(() => import('@/pages/patients/PatientDetailPage').then(m => ({ default: m.PatientDetailPage })));
const MeasurementFormPage = lazy(() => import('@/pages/patients/MeasurementFormPage').then(m => ({ default: m.MeasurementFormPage })));
const AnamnesisFormPage = lazy(() => import('@/pages/patients/AnamnesisFormPage').then(m => ({ default: m.AnamnesisFormPage })));
const DietsListPage = lazy(() => import('@/pages/diets/DietsListPage').then(m => ({ default: m.DietsListPage })));
const DietFormPage = lazy(() => import('@/pages/diets/DietFormPage').then(m => ({ default: m.DietFormPage })));
const DietEditorPage = lazy(() => import('@/pages/diets/DietEditorPage').then(m => ({ default: m.DietEditorPage })));
const FoodsListPage = lazy(() => import('@/pages/foods/FoodsListPage').then(m => ({ default: m.FoodsListPage })));
const AppointmentsPage = lazy(() => import('@/pages/appointments/AppointmentsPage').then(m => ({ default: m.AppointmentsPage })));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper (redirects to app if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    let mounted = true;
    let revision = 0;
    setLoading(true);

    // Keep SDK calls out of the auth callback to avoid holding its session lock.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const requestRevision = ++revision;
      if (!mounted) return;
      if (!session?.user) {
        setUser(null);
        usePatientStore.getState().setPatients([]);
        usePatientStore.getState().selectPatient(null);
        queryClient.clear();
        return;
      }

      void fetchSupabase('nutry_profiles', {
        filters: `id=eq.${session.user.id}`,
        single: true,
        accessToken: session.access_token,
      }).then(({ data, error }) => {
        if (!mounted || requestRevision !== revision) return;
        setUser(error ? null : data);
      });
    });

    return () => {
      mounted = false;
      revision++;
      subscription.unsubscribe();
    };
  }, [setUser, setLoading]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center" role="status">Caricamento...</div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />

          <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
          <Route path="/reset-password" element={<PasswordRecoveryPage reset />} />

          {/* Protected Routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Patients */}
            <Route path="patients" element={<PatientsListPage />} />
            <Route path="patients/new" element={<PatientFormPage />} />
            <Route path="patients/:id" element={<PatientDetailPage />} />
            <Route path="patients/:id/edit" element={<PatientFormPage />} />
            <Route path="patients/:id/measurements/new" element={<MeasurementFormPage />} />
            <Route path="patients/:id/anamnesis" element={<AnamnesisFormPage />} />

            {/* Diets */}
            <Route path="diets" element={<DietsListPage />} />
            <Route path="diets/new" element={<DietFormPage />} />
            <Route path="diets/:id" element={<DietEditorPage />} />

            {/* Foods */}
            <Route path="foods" element={<FoodsListPage />} />

            {/* Appointments */}
            <Route path="appointments" element={<AppointmentsPage />} />

            {/* Reports */}
            <Route path="reports" element={<ReportsPage />} />

            {/* Settings */}
            <Route path="settings" element={<SettingsPage />} />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
